import ReportFilter, {
    FilterField,
    FilterRecord,
    FilterValue,
} from 'components/reports/common/ReportFilter'
import { updateUrlWithFilters } from 'components/reports/common/utils'
import {
    CHECK_STATUSES,
    CHECK_TYPES,
    COUNTRIES,
    OUTCOMES,
    PROVIDER_STATUSES,
    runLabel,
} from 'components/reports/trustCheck/trustCheckLabels'
import {
    fetchTrustCheckReport,
    fetchTrustCheckSummary,
    Filters,
} from 'features/reports/trustCheckReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import queryString from 'query-string'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

const toFilterRecord = (filters: Filters): FilterRecord => ({
    run_id: filters.run_id,
    country: filters.country,
    outcome: filters.outcome,
    check_type: filters.check_type,
    check_status: filters.check_status,
    sub_signal: filters.sub_signal,
    current_status: filters.current_status,
    bad_actor: filters.bad_actor,
    search: filters.search,
})

const TrustCheckReportFilter = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const { filters, pagination, isLoading, sorting, runs } = useAppSelector(
        (state) => state.trustCheckReport,
    )

    const handleUrlUpdate = useCallback(
        (field: string, value: FilterValue) => {
            const currentQuery = queryString.parse(window.location.search, {
                arrayFormat: 'bracket',
            })
            updateUrlWithFilters(location, field, value, currentQuery)
        },
        [location],
    )

    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(fetchTrustCheckReport({ filters, page, sorting }))
        },
        [dispatch, filters, sorting],
    )

    const handleFetchData = useCallback(
        (newFilters: FilterRecord, page: number = 0) => {
            dispatch(fetchTrustCheckReport({ filters: newFilters, page, sorting }))
            dispatch(
                fetchTrustCheckSummary({
                    runId: newFilters.run_id as number | string | undefined,
                    country: newFilters.country as string | undefined,
                }),
            )
        },
        [dispatch, sorting],
    )

    const fields = useMemo(
        (): FilterField[] => [
            {
                key: 'run_id',
                type: 'select',
                placeholder: 'Run',
                width: 'min-w-[360px]',
                options: [
                    { value: '', name: 'Latest completed run' },
                    ...runs.map((run) => ({ value: run.trust_check_run_id, name: runLabel(run) })),
                ],
            },
            {
                key: 'country',
                type: 'select',
                placeholder: 'Market',
                width: 'w-[150px]',
                options: [
                    { value: '', name: 'All markets' },
                    ...COUNTRIES.map((code) => ({ value: code, name: code })),
                ],
            },
            {
                key: 'outcome',
                type: 'select',
                placeholder: 'Outcome',
                width: 'w-[210px]',
                options: [{ value: '', name: 'Any outcome' }, ...OUTCOMES],
            },
            {
                key: 'check_type',
                type: 'select',
                placeholder: 'Check',
                width: 'w-[230px]',
                options: [{ value: '', name: 'Any check' }, ...CHECK_TYPES],
            },
            {
                key: 'check_status',
                type: 'select',
                placeholder: 'Check result',
                width: 'w-[230px]',
                options: [{ value: '', name: 'Any result (needs a check)' }, ...CHECK_STATUSES],
            },
            {
                key: 'sub_signal',
                type: 'text',
                placeholder: 'Sub-signal, e.g. iban_shared',
                width: 'w-[210px]',
            },
            {
                key: 'current_status',
                type: 'select',
                placeholder: 'Current status',
                width: 'w-[190px]',
                options: [{ value: '', name: 'Any current status' }, ...PROVIDER_STATUSES],
            },
            {
                key: 'bad_actor',
                type: 'select',
                placeholder: 'Bad actor',
                width: 'w-[190px]',
                options: [
                    { value: '', name: 'Bad actor: any' },
                    { value: 1, name: 'Known bad actors' },
                    { value: 0, name: 'Not labelled' },
                ],
            },
            {
                key: 'search',
                type: 'text',
                placeholder: 'Provider id or business name',
                width: 'w-[230px]',
            },
        ],
        [runs],
    )

    return (
        <ReportFilter
            fields={fields}
            initialFilters={toFilterRecord(filters)}
            pagination={pagination}
            onPageChange={handlePageChange}
            fetchData={handleFetchData}
            isLoading={isLoading}
            onUrlUpdate={handleUrlUpdate}
        />
    )
}

export default TrustCheckReportFilter
